import type { ReadinessState } from './useGuardianReadiness';
import type { ProvisionStage } from './provisionStatus';
import { runtimeEnv } from '../runtimeEnv';

// The hub-side view of one product being provisioned: which product is being watched,
// where its readiness poll stands (plus any step-by-step stages the status endpoint
// reports), and the controls the scenarios use to drive it.
export type SetupWatch = {
  productKey: string | null;
  state: ReadinessState;
  stages: ProvisionStage[];
  watch: (productKey: string) => void;
  dismiss: () => void;
};

export type CardSetupState = 'building' | 'stalled';

type StatusUrlBuilder = (orgSlug: string) => string;

// Per-product builders for the org's provisioning status endpoint. With nothing
// registered the readiness hook stays idle and the interim timer covers the wait.
const statusUrlBuilders = new Map<string, StatusUrlBuilder>();

export function registerProvisioningStatusUrl(productKey: string, build: StatusUrlBuilder): void {
  statusUrlBuilders.set(productKey, build);
}

export function provisioningStatusUrl(productKey: string, orgSlug: string | null): string | null {
  const build = statusUrlBuilders.get(productKey);
  return build && orgSlug ? build(orgSlug) : null;
}

// Guardian's build status is served by the entitlements API, which reads the org from
// the caller's token — the slug the builder receives is only the readiness gate above.
registerProvisioningStatusUrl('guardian', () => `${runtimeEnv.entitlementsApiUrl}/provisioning-status`);

// Products whose trial stands up per-org infrastructure before they are usable. Instant
// products skip the setting-up wait entirely — their trial is usable the moment it
// registers.
const PROVISIONED_PRODUCT_KEYS = new Set(['guardian']);

export function productIsProvisioned(productKey: string): boolean {
  return PROVISIONED_PRODUCT_KEYS.has(productKey);
}

export function cardSetupState(productKey: string, setup: SetupWatch): CardSetupState | undefined {
  if (setup.productKey !== productKey) return undefined;
  if (setup.state === 'checking') return 'building';
  if (setup.state === 'timed-out') return 'stalled';
  return undefined;
}
